import { useMutation } from '@tanstack/react-query'
import { App, Button, Flex, Form, Input } from 'antd'
import { createStyles } from 'antd-style'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  mfaVerifyBackupCodeCreate,
  usersLoginAdminCreateMutation
} from 'src/api/main'
import { LOGIN_IN_PROGRESS_KEY } from 'src/constants'
import { useSessionContext } from 'src/hooks'
import { paths } from 'src/router'
import { setLocalStorageToken, useInlineApiErrors } from 'src/utils'

import { AuthHeader } from '../components/AuthHeader'
import { schedulePasskeyPrompt } from '../components/PasskeyRecommendationModal'
import { asAuthenticatedTokens } from '../utils/authResponseAdapters'

const useStyles = createStyles(() => ({
  form: {
    maxWidth: 360
  },
  otp: {
    display: 'grid',
    gridTemplateColumns: 'repeat(8, minmax(0, 1fr))',
    gap: 8,
    width: '100%',
    'input.ant-otp-input': {
      width: '100% !important',
      height: '36px !important',
      minWidth: 0
    }
  }
}))

type MfaLocationState = {
  __mfaAccessToken?: string
}

export const ConfirmMfaBackupCode = () => {
  const { t } = useTranslation()
  const { styles } = useStyles()
  const [form] = Form.useForm()
  const navigate = useNavigate()
  const location = useLocation()
  const { message } = App.useApp()
  const { showApiErrors, errorAlert } = useInlineApiErrors()
  const { updateUserData } = useSessionContext()

  const locationState = useMemo(
    () => location.state as MfaLocationState | null,
    [location.state]
  )

  const mfaHeaders = useMemo(
    () =>
      locationState?.__mfaAccessToken
        ? { Authorization: `Token ${locationState.__mfaAccessToken}` }
        : undefined,
    [locationState]
  )

  useEffect(() => {
    if (locationState?.__mfaAccessToken) return
    navigate(paths.auth.login, { replace: true })
  }, [locationState, navigate])

  const { mutateAsync: fetchUserAfterLogin, isPending: isPendingUser } =
    useMutation({
      ...usersLoginAdminCreateMutation(),
      onSuccess: (response) => {
        sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
        updateUserData(response)
        message.success(t('login_success'))
        navigate(paths.home)
      },
      onError: (e) => {
        sessionStorage.removeItem(LOGIN_IN_PROGRESS_KEY)
        showApiErrors(e)
      }
    })

  const { mutate: verifyBackupCode, isPending: isVerifyPending } = useMutation({
    mutationFn: async (values: { backup_code: string }) => {
      const { data } = await mfaVerifyBackupCodeCreate({
        headers: mfaHeaders,
        body: { backup_code: values.backup_code },
        throwOnError: true
      })

      return data
    },
    onSuccess: async (response) => {
      const tokens = asAuthenticatedTokens(response)

      if (tokens) {
        schedulePasskeyPrompt()
        setLocalStorageToken(tokens)
        await fetchUserAfterLogin({})
        return
      }

      showApiErrors(new Error('Missing tokens in MFA response'))
    },
    onError: (error) => {
      const axiosError = error as {
        response?: { data?: { code?: string; detail?: string } }
      }
      const code = axiosError.response?.data?.code

      if (code === 'token_not_valid') {
        message.error(t('auth.mfa.tokenExpired'))
        navigate(paths.auth.login, { replace: true })
        return
      }

      const detail = axiosError.response?.data?.detail

      if (detail) {
        form.setFields([{ name: 'backup_code', errors: [detail] }])
        return
      }

      showApiErrors(error)
    }
  })

  const onSubmit = async () => {
    try {
      const values = await form.validateFields()

      verifyBackupCode(values)
    } catch {
      // Validation errors shown by form
    }
  }

  return (
    <Flex vertical gap={16} style={{ width: '100%' }}>
      <AuthHeader
        title={t('auth.mfa.backupCodeTitle')}
        subtitle={t('auth.mfa.backupCodeDescription')}
      />

      {errorAlert}

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        className={styles.form}
      >
        <Form.Item
          name="backup_code"
          label={t('auth.mfa.backupCodeLabel')}
          rules={[
            { required: true, message: t('auth.mfa.backupCodeRequired') }
          ]}
        >
          <Input.OTP
            autoFocus
            length={8}
            size="large"
            formatter={(str) => str.toUpperCase()}
            className={styles.otp}
            onChange={(value) => {
              if (value.length === 8 && !isVerifyPending) {
                verifyBackupCode({ backup_code: value })
              }
            }}
          />
        </Form.Item>

        <Button
          block
          size="large"
          type="primary"
          loading={isVerifyPending || isPendingUser}
          onClick={onSubmit}
        >
          {t('sign_in')}
        </Button>
      </Form>
    </Flex>
  )
}
